import { useCallback, useMemo } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { MoreHoriz } from "@livechat/design-system-icons";
import {
  ActionMenu,
  ActionMenuItem,
  Button,
  IActionMenuOption,
  Icon,
  Tooltip,
} from "@livechat/design-system-react-components";

import { EventPlace, trackEngagement } from "helpers/analytics";
import { openInNewCard } from "helpers/routing";
import { useToggle } from "hooks/use-toggle";
import { SettingsRoutes } from "routes/settings/constants";
import {
  IApplication,
  IWithApplicationsState,
  getAppDesignationUrl,
  isApplicationInstalled,
} from "store/entities/applications";
import { ApplicationsActions } from "store/features/applications/actions";
import { selectIsGhostLogin } from "store/features/session";

import { DropdownOptions, DropdownOptionsContent } from "../constants";
import { handleDirectInstallationUrlFlow, isDefaultIntegration } from "../helpers";

import UninstallModal from "./UninstallModal";

interface IProps {
  application: IApplication;
  shouldRedirect?: boolean;
}

export const ActionsDropdown: React.FC<IProps> = ({ application, shouldRedirect }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isGhost = useSelector(selectIsGhostLogin);
  const [isDropdownOpen, toggleIsDropdownOpen] = useToggle(false);
  const [isUninstallModalOpen, toggleIsUninstallModalOpen] = useToggle(false);
  const eventPlace = shouldRedirect ? EventPlace.AppDetails : EventPlace.AppList;
  const eventPlaceNormed = {
    [EventPlace.AppList]: "appList",
    [EventPlace.AppDetails]: "appDetails",
  };

  const isInstalled = useSelector((state: IWithApplicationsState) => isApplicationInstalled(state, application?.id));

  const appDesignationUrl = useSelector((state: IWithApplicationsState) =>
    getAppDesignationUrl(state, application?.id)
  );

  const pickAction = useCallback(
    (itemId: string) => {
      const { id, name, tutorialUrl, directInstallUrl, clientId, goToWebsiteUrl, publishedAt } = application;
      const slug = publishedAt ? application?.slug : id;

      switch (itemId) {
        case DropdownOptions.DirectInstallUrl:
          trackEngagement("Integrations: Finish installation clicked", eventPlace, {
            appName: name,
            eventPlace: eventPlaceNormed[eventPlace],
          });
          // @ts-expect-error TS(2322)
          handleDirectInstallationUrlFlow({ name, directInstallUrl, clientId });
          break;

        case DropdownOptions.SetupInstructions:
          trackEngagement("Integrations: Setup instructions clicked", eventPlace, {
            appName: name,
            eventPlace: eventPlaceNormed[eventPlace],
          });
          // @ts-expect-error TS(2345)
          openInNewCard(tutorialUrl);
          break;

        case DropdownOptions.Install:
          dispatch(ApplicationsActions.installApplication(id));
          break;

        case DropdownOptions.Uninstall:
          toggleIsUninstallModalOpen();
          break;

        case DropdownOptions.Settings:
          history.push(`${SettingsRoutes.Integrations}/apps/${slug}/`);
          break;

        case DropdownOptions.OpenInMarketplace:
          history.push(`/marketplace${appDesignationUrl}${slug}/`);
          break;

        case DropdownOptions.GoToApp:
          // @ts-expect-error TS(2345)
          openInNewCard(goToWebsiteUrl);
          break;

        case DropdownOptions.ContactDeveloper:
          trackEngagement("Integrations: Contact developer clicked", eventPlace, {
            appName: name,
            eventPlace: eventPlaceNormed[eventPlace],
          });
          break;

        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [application, appDesignationUrl, dispatch, history]
  );

  const getActionMenuOptions = (
    application: IApplication,
    isInstalled: boolean,
    handleItemSelect: (key: string) => void
  ): IActionMenuOption[] => {
    if (!application) {
      return [];
    }

    const { directInstallUrl, tutorialUrl, goToWebsiteUrl, id } = application;

    let items: Partial<IActionMenuOption>[] = [];
    const isDefaultApplication = isDefaultIntegration(id);

    if (!isDefaultApplication) {
      items.push({ key: DropdownOptions.OpenInMarketplace });
    }

    if (tutorialUrl) {
      items.push({ key: DropdownOptions.SetupInstructions });
    }

    if (isInstalled && directInstallUrl) {
      items.push({ key: DropdownOptions.DirectInstallUrl });
    }

    if (goToWebsiteUrl) {
      items.push({ key: DropdownOptions.GoToApp });
    }

    if (!isDefaultApplication) {
      if (isInstalled) {
        items.push({ key: DropdownOptions.Uninstall });
      } else {
        items.push({ key: DropdownOptions.Install });
      }
    }

    items = items.map(item => {
      item.element = (
        <ActionMenuItem kind={item.key === DropdownOptions.Uninstall ? "warning" : undefined}>
          {DropdownOptionsContent[item.key as DropdownOptions]}
        </ActionMenuItem>
      );
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      item.onClick = () => handleItemSelect(item.key as string);
      return item;
    });

    return items as IActionMenuOption[];
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleItemSelect = (key: string) => {
    pickAction(key);
    toggleIsDropdownOpen();
  };

  const options = useMemo(
    () => getActionMenuOptions(application, isInstalled, handleItemSelect),
    [application, isInstalled, handleItemSelect]
  );

  const optionsAreEmpty = options?.length === 0;
  const isTriggerButtonDisabled = optionsAreEmpty || isGhost;

  return (
    <>
      <ActionMenu
        visible={isDropdownOpen}
        placement="bottom-end"
        onClose={toggleIsDropdownOpen}
        options={options}
        triggerRenderer={
          <Tooltip
            kind="important"
            placement="left"
            isVisible={!optionsAreEmpty && isGhost ? undefined : false}
            triggerRenderer={() => (
              <Button
                disabled={isTriggerButtonDisabled}
                kind="plain"
                onClick={toggleIsDropdownOpen}
                size="compact"
                icon={<Icon source={MoreHoriz} />}
              />
            )}
          >
            Action blocked for security reasons (ghost login)
          </Tooltip>
        }
      />

      {isUninstallModalOpen && (
        <UninstallModal
          onClose={toggleIsUninstallModalOpen}
          shouldRedirect={shouldRedirect}
          application={application}
        />
      )}
    </>
  );
};

export default ActionsDropdown;
